import type { ArchitectureData, DrillLevel } from "@/lib/arch-parser";

type ResolvedKind =
  | "system"
  | "actor"
  | "external-system"
  | "container"
  | "component"
  | "question"
  | "unknown";

interface Resolved {
  id: string;
  kind: ResolvedKind;
  containerId?: string;
  level: DrillLevel;
}

export function resolveId(arch: ArchitectureData, id: string): Resolved {
  if (arch.system?.id === id) {
    return { id, kind: "system", level: 1 };
  }
  if (arch.actors.some((a) => a.id === id)) {
    return { id, kind: "actor", level: 1 };
  }
  if (arch.externalSystems.some((s) => s.id === id)) {
    return { id, kind: "external-system", level: 1 };
  }
  const container = arch.containers.find((c) => c.id === id);
  if (container) {
    return { id, kind: "container", level: 2 };
  }
  for (const c of arch.containers) {
    if (c.components.some((comp) => comp.id === id)) {
      return { id, kind: "component", containerId: c.id, level: 3 };
    }
  }
  // v0.3 lifts components to a top-level collection — check it for completeness
  // (covers v0.3 docs where the container's nested components[] may be empty).
  if (arch.components?.some((comp) => comp.id === id)) {
    const containerId = arch.components.find((comp) => comp.id === id)?.container;
    return { id, kind: "component", containerId, level: 3 };
  }
  // Questions are panel-only — no graph node, but they can still anchor comments
  // and be selected via the URL fragment / comment thread links.
  if (arch.questions?.some((q) => q.id === id)) {
    return { id, kind: "question", level: 1 };
  }
  return { id, kind: "unknown", level: 1 };
}
